import { Params, Router } from '@angular/router';

import { ERROR_CTA_ACTIONS } from '@fcom/common/components';
import { RootPaths } from '@fcom/core';
import { FinnairOrder, FinnairTravelRequirementsEligibility } from '@fcom/dapi/api/models';
import { bookingHasDisruption } from '@fcom/common-booking/utils/common-booking.utils';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { CustomServiceType } from '@fcom/dapi';
import { isOrderLessThan24HoursOfCreation } from '@fcom/common/utils';

import { MmbNavigationItem } from '../interfaces';
import { MmbStage } from '../enums';

export const defaultQueryParams: Params = {
  cta: 'errors.generic.cta',
  info: 'errors.booking.description',
  title: 'sorry',
  subtitle: 'errors.booking.title',
  ctaAction: ERROR_CTA_ACTIONS.NAVIGATE_TO_MANAGE_BOOKING,
};

export const navigateToError = (router: Router, lang: string, error: string, queryParams: Params = {}): void => {
  router.navigate([lang, RootPaths.MANAGE_BOOKING_ROOT, 'error', error], {
    skipLocationChange: true,
    queryParams: { ...defaultQueryParams, ...queryParams },
  });
};

export const canViewTravelPreparations = (order: FinnairOrder): boolean =>
  !bookingHasDisruption(order) &&
  Object.entries(order.eligibilities.travelRequirement).some(
    ([key, eligibility]: [string, FinnairTravelRequirementsEligibility]) =>
      key !== 'emergencyContactDetails' && eligibility.required
  );

export const canViewCarRentals = (order: FinnairOrder): boolean => {
  const carRentalElibible = order.eligibilities?.carRental?.find((eligibility) => {
    return eligibility.id === CustomServiceType.JOURNEY && eligibility.isAllowedToUse;
  });

  const airportTransfersEligible = order.eligibilities?.airportTransfer?.find((eligibility) => {
    return eligibility.id === CustomServiceType.JOURNEY && eligibility.isAllowedToUse;
  });

  return !isOrderLessThan24HoursOfCreation(order) && Boolean(carRentalElibible || airportTransfersEligible);
};

export const getMmbNavigationItems = (order: FinnairOrder, enableExpediaWidget: boolean): MmbNavigationItem[] =>
  [
    {
      targetStage: MmbStage.VIEW,
      path: `${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`,
      translationKey: 'tripOverview',
    },
    {
      targetStage: MmbStage.PASSENGERS,
      path: `${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW_PASSENGERS.path}`,
      translationKey: 'passengerDetails',
    },
    canViewTravelPreparations(order) && {
      targetStage: MmbStage.TRAVEL_PREPARATION,
      path: `${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.TRAVEL_PREPARATION.path}`,
      translationKey: 'travelPreparations',
    },
    canViewCarRentals(order) && {
      targetStage: MmbStage.CAR_RENTAL,
      path: `${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.CAR_RENTAL.path}`,
      translationKey: enableExpediaWidget ? 'hotelsAndCarRental' : 'carRental',
    },
    {
      targetStage: MmbStage.LOGIN,
      path: `${RootPaths.MANAGE_BOOKING_ROOT}`,
      translationKey: 'login',
    },
  ].filter(Boolean);
